"use client";

import { MODAL_IDS } from "@wtc/ui";

import { buildActionLaunchHints } from "@/lib/actions/launch/buildActionLaunchHints";
import { getActionManifest, hasActionManifest } from "@/lib/actions/manifests";
import {
  canDirectClientRedirect,
  runClientRedirectFastPath,
} from "@/lib/actions/manifests/clientRedirectFastPath";
import type { ClientRunHooks } from "@/lib/actions/manifests/types";
import type { ResolverEntityKey } from "@/lib/actions/relationships/types";
import "@/lib/actions/manifests"; // side-effect: register manifests + handlers

import { getActionHandler, isActionSupported } from "./index-handlers";
import type { BaseEntity, DetailAction, DetailActionContext } from "../types/detailPage";

/**
 * Map UniversalEntityPage entityName → resolver entity key. Add to this when
 * a new ResolverEntityKey gets a corresponding entity page.
 */
const RESOLVER_ENTITY_MAP: Record<string, string> = {
  contacts: "contact",
  companies: "company",
  deals: "deal",
  tickets: "ticket",
  communications: "communication",
  time_entries: "time_entry",
  tasks: "task",
  recurring_charges: "recurring_charge",
  recurring_transactions: "recurring_transaction",
};

function getActionLabel<T extends BaseEntity>(action: DetailAction<T>, item: T): string {
  return typeof action.label === "function" ? action.label(item) : action.label;
}

function buildClientRunHooks<T extends BaseEntity>(
  context: DetailActionContext<T>,
): ClientRunHooks {
  return {
    openModal: (modalId, data) => context.onOpenModal(modalId, data),
    closeModal: (modalId) => context.onCloseModal(modalId),
    onNavigate: (href) => context.onNavigate(href),
    onToast: (message, type) => context.onToast(message, type),
  };
}

export async function executeDetailAction<T extends BaseEntity>(
  action: DetailAction<T>,
  context: DetailActionContext<T>,
  options?: { suppressDefaultSuccessToast?: boolean }
): Promise<boolean> {
  const { item } = context;
  const label = getActionLabel(action, item);
  let hasToastFeedback = false;
  const trackedContext: DetailActionContext<T> = {
    ...context,
    onToast: (message, type) => {
      hasToastFeedback = true;
      context.onToast(message, type);
    },
  };

  if (action.href) {
    const href = typeof action.href === "function" ? action.href(item, trackedContext) : action.href;
    if (action.openInNewTab) {
      window.open(href, "_blank", "noopener,noreferrer");
    } else {
      trackedContext.onNavigate(href);
    }
    trackedContext.onToast(`Opening ${label.toLowerCase()}...`, "info");
    return true;
  }

  // Cross-entity action resolver — if this action has a manifest, route
  // through the resolver modal instead of the legacy single-entity handler.
  // Resolver walks the relationship graph (e.g. deal → company → contact),
  // collects missing fields, and runs the cross-entity handler.
  //
  // UX bypass: when the source entity IS the manifest's target AND the action
  // has no extra inputs AND it's not a bulk action, skip the resolver so the
  // existing single-entity handler runs (no extra confirm step). SMS on a
  // contact has extras (message body), so it still opens the resolver.
  // Actions without a manifest fall through to legacy dispatch below.
  if (hasActionManifest(action.id)) {
    const manifest = getActionManifest(action.id);
    const sourceEntityName =
      context.pageConfig?.entityName ?? context.surfaceEntityType ?? undefined;
    const sourceEntity = sourceEntityName
      ? RESOLVER_ENTITY_MAP[sourceEntityName]
      : undefined;
    const sourceId = (item as unknown as { id?: string }).id;

    const skip =
      !!manifest &&
      sourceEntity === manifest.requires.entity &&
      !manifest.requires.allowMulti &&
      (manifest.requires.extraInputs?.length ?? 0) === 0;

    if (
      manifest &&
      sourceEntity &&
      sourceId &&
      canDirectClientRedirect(manifest, sourceEntity as ResolverEntityKey)
    ) {
      const entityName =
        context.pageConfig?.entityName ?? context.surfaceEntityType;
      if (entityName) {
        trackedContext.onCloseModal(`${entityName}-detail`);
      }

      const launchHints = buildActionLaunchHints(
        action.id,
        trackedContext,
        trackedContext.queryClient,
      );

      const fastPath = await runClientRedirectFastPath(
        manifest,
        {
          actionId: action.id,
          sourceEntity: sourceEntity as ResolverEntityKey,
          sourceId,
        },
        buildClientRunHooks(trackedContext),
        launchHints,
      );

      if (fastPath.ok) {
        return true;
      }
      if (fastPath.reason === "error") {
        trackedContext.onToast(
          fastPath.message ?? `Failed to ${label.toLowerCase()}`,
          "error",
        );
        return false;
      }
      // needs_ui — open resolver modal below
    }

    // validate-action-modal-mounts-allow: action-resolver verified mounted (ActionResolverModal in EntityPageModals; binds id via the resolver hook, not modalId=)
    if (!skip && sourceEntity && sourceId) {
      trackedContext.onOpenModal(MODAL_IDS.actions.resolver, {
        actionId: action.id,
        sourceEntity,
        sourceId,
      });
      return true;
    }
    // Fall through to legacy handler.
  }

  if (!action.action) {
    trackedContext.onToast(`Action ${label.toLowerCase()} has no implementation`, "warning");
    return false;
  }

  try {
    if (typeof action.action === "string") {
      if (!isActionSupported(action.action)) {
        trackedContext.onToast(`Action ${action.action} not implemented`, "warning");
        return false;
      }

      const handler = getActionHandler(action.action);
      if (!handler) {
        trackedContext.onToast(`Action ${action.action} not implemented`, "warning");
        return false;
      }

      await handler(item, trackedContext);
    }

    if (!hasToastFeedback && !options?.suppressDefaultSuccessToast) {
      trackedContext.onToast(`${label} completed`, "success");
    }
    return true;
  } catch (error) {
    if (!hasToastFeedback) {
      trackedContext.onToast(`Failed to execute ${label.toLowerCase()}`, "error");
    }
    console.error(`Error executing action ${action.id}:`, error);
    return false;
  }
}
